import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth, getUserSettings, updateUserSettings } from "../api/Api";
import { UserSettings, PutUserSettingsRequest, CEFRLevel } from "@shared_types";

const USER_SETTINGS_QUERY_KEY = "userSettings";

/**
 * Default user settings — mirrors DEFAULT_USER_SETTINGS in the native client
 * (client-native/src/stores/globalStore.ts). Server responses are merged ON TOP
 * of these so that any field the server omits (e.g. for older accounts) falls
 * back to the same default the iOS app uses, instead of reading as `false`.
 */
export const DEFAULT_USER_SETTINGS: UserSettings = {
  userId: 0,
  isSoundEffectsEnabled: true,
  isWordListEnabled: true,
  isGuessTheWordEnabled: true,
  isFillInTheBlankEnabled: true,
  isMatchTheWordsEnabled: true,
  isSpellTheWordEnabled: true,
  // Kept intentionally after the "Repeat after Me" activity was removed: this is
  // a required field of the shared `UserSettings` DTO (the server still sends and
  // persists it, and iOS keeps the same dormant default). Dropping it would break
  // the type and the settings round-trip; a real removal is a backend/schema change.
  isRepeatAfterMeEnabled: true,
  isBuildTheSentenceEnabled: true,
  showWordTutorialNextLesson: true,
  showFeedTutorialNextLesson: true,
  userFeedLevel: "No Level" as CEFRLevel,
  userFeedDialect: "unknown",
  areFeedActivitiesEnabled: true,
  activityCount: 3,
  userFeedTags: [],
  hasCompletedFeedTagsOnboarding: false,
  isTutorialDismissed: false,
  voiceAccent: "mexican",
  voiceGender: "female",
  isFlightSchoolEnabled: false,
  dailyListeningGoalMinutes: 5,
  wordClickTutorialComplete: false,
  phraseHighlightTutorialComplete: false,
  activationStep: 1,
  highestEarnedLevel: 1,
};

export interface UseUserSettingsReturn {
  settings: UserSettings | undefined;
  isLoading: boolean;
  error: unknown;
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>;
}

/**
 * Hook for managing user settings
 * @returns {UseUserSettingsReturn} The user settings and functions to update them
 */
export const useUserSettings = (): UseUserSettingsReturn => {
  const queryClient = useQueryClient();

  // Fetch user settings
  const { data, isLoading, error } = useQuery({
    queryKey: [USER_SETTINGS_QUERY_KEY],
    queryFn: () => callWithAuth(getUserSettings),
    staleTime: 60000, // Keep cache fresh for 1 minute
  });

  // Mutation for updating user settings
  const mutation = useMutation({
    mutationFn: (newSettings: Partial<UserSettings>) => {
      const currentSettings = data?.settings;
      if (!currentSettings) {
        throw new Error("Cannot update settings before they are loaded");
      }

      // Base the update on defaults + server settings so a partial update never
      // drops a field the server omitted (matches the native client).
      const updatedSettings: UserSettings = {
        ...DEFAULT_USER_SETTINGS,
        ...currentSettings,
        ...newSettings,
      };

      const request: PutUserSettingsRequest = {
        settings: updatedSettings,
      };

      return callWithAuth(updateUserSettings, request);
    },
    // Optimistically update the cache for immediate UI feedback
    onMutate: async (newPartialSettings) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: [USER_SETTINGS_QUERY_KEY] });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData([
        USER_SETTINGS_QUERY_KEY,
      ]);

      // Optimistically update the settings in the cache
      if (data?.settings) {
        queryClient.setQueryData([USER_SETTINGS_QUERY_KEY], {
          settings: {
            ...data.settings,
            ...newPartialSettings,
          },
        });
      }

      // Return the snapshot so we can rollback on error
      return { previousSettings };
    },
    // If the mutation fails, roll back to the previous value
    onError: (err, _newSettings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(
          [USER_SETTINGS_QUERY_KEY],
          context.previousSettings
        );
      }
      console.error("Error updating user settings:", err);
    },
    // After success or failure, invalidate the query to ensure fresh data
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [USER_SETTINGS_QUERY_KEY] });
    },
  });

  // Function to update settings
  const updateSettings = async (newSettings: Partial<UserSettings>) => {
    await mutation.mutateAsync(newSettings);
  };

  // Merge server settings on top of defaults so undefined toggles read as their
  // iOS-equivalent default (most are `true`) rather than `false`. Stays
  // `undefined` while loading so callers can still distinguish that state.
  const settings = data?.settings
    ? { ...DEFAULT_USER_SETTINGS, ...data.settings }
    : undefined;

  return {
    settings,
    isLoading,
    error,
    updateSettings,
  };
};
